import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import styles from './modal.module.scss';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  width?: number;
  noBorder?: boolean;
  bodyClassName?: string;
  hasCloseIcon?: boolean;
}

export const Modal = ({
  open,
  onClose,
  title,
  children,
  width = 500,
  noBorder = false,
  bodyClassName = '',
  hasCloseIcon = true,
}: ModalProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (open) {
      setIsVisible(true);
      setIsAnimating(true);
      return;
    }
    if (isVisible) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [open, isVisible]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  if (!isVisible) return null;

  const modalContent = (
    <div
      className={`${styles.modalOverlay} ${
        !isAnimating ? styles.modalClosing : ''
      }`}
      onClick={onClose}
    >
      <div
        className={`${styles.modalContainer} ${
          isAnimating ? styles.modalOpen : styles.modalClose
        }`}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{ maxWidth: `${width}px` }}
      >
        <div
          className={
            noBorder ? styles.modalHeaderWithoutBorder : styles.modalHeader
          }
        >
          <h2 className={styles.modalTitle}>{title}</h2>
          {hasCloseIcon && (
            <button
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Fechar modal"
            >
              <FontAwesomeIcon icon={faXmark} size="lg" />
            </button>
          )}
        </div>

        <div className={`${styles.modalBody} ${bodyClassName}`}>{children}</div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
